/**
 * OAuth 2.0 Authorization Code + PKCE redirect helpers.
 *
 * Replaces the GIS Token Model on Safari/iOS PWA where ITP breaks the
 * silentRefresh iframe. The new flow:
 *
 *   1. User clicks "Sign in with Google"
 *   2. We generate code_verifier + code_challenge, persist verifier in
 *      sessionStorage, redirect to accounts.google.com
 *   3. Google redirects back to /auth/callback?code=<code>
 *   4. AuthCallbackScreen reads stored verifier, POSTs (code, verifier) to
 *      /api/auth/exchange — server uses client_secret to mint refresh_token
 *      and sets the encrypted cookie
 *
 * Sharp edges:
 *   - code_verifier MUST be 43–128 chars of [A-Z a-z 0-9 - . _ ~] per RFC 7636.
 *     We use 43 base64url chars from 32 random bytes.
 *   - code_challenge = base64url(SHA-256(verifier)) with `S256` method —
 *     we never use `plain` (a verifier-leak attacker would defeat it).
 *   - sessionStorage write may throw under iOS Lockdown / private mode;
 *     persistVerifier swallows errors so the click handler stays unbroken,
 *     and the callback path detects missing verifier and routes to error.
 */

const SHEETS_SCOPE = 'https://www.googleapis.com/auth/spreadsheets';
const DRIVE_FILE_SCOPE = 'https://www.googleapis.com/auth/drive.file';
const REQUIRED_SCOPES = `openid email profile ${SHEETS_SCOPE} ${DRIVE_FILE_SCOPE}`;

const GOOGLE_AUTHORIZE_ENDPOINT = 'https://accounts.google.com/o/oauth2/v2/auth';

export const PKCE_VERIFIER_STORAGE_KEY = 'mochia.pkce.verifier.v1';

/**
 * Generate a 43-char base64url string from 32 random bytes — within RFC 7636's
 * 43–128 length range.
 */
export function generateCodeVerifier(): string {
  const bytes = new Uint8Array(32);
  // jsdom and modern browsers expose globalThis.crypto.getRandomValues.
  globalThis.crypto.getRandomValues(bytes);
  return base64urlFromBytes(bytes);
}

/**
 * Compute the S256 code_challenge from the verifier:
 *   challenge = base64url(SHA-256(verifier))
 *
 * Async because WebCrypto SubtleCrypto.digest is async — we don't use a sync
 * shim because the verifier is generated inside the click handler and the
 * redirect is async anyway.
 */
export async function computeCodeChallenge(verifier: string): Promise<string> {
  const enc = new TextEncoder().encode(verifier);
  const hash = await globalThis.crypto.subtle.digest('SHA-256', enc);
  return base64urlFromBytes(new Uint8Array(hash));
}

export interface BuildAuthorizeUrlInput {
  clientId: string;
  redirectUri: string;
  codeChallenge: string;
}

/**
 * Build the URL to redirect the browser to. We request:
 *   - response_type=code   (Authorization Code flow)
 *   - access_type=offline  (so Google issues a refresh_token)
 *   - prompt=consent       (force re-consent on each first sign-in to ensure
 *                           refresh_token is returned even on subsequent grants)
 *   - code_challenge_method=S256 (never `plain`)
 *   - include_granted_scopes=true (so previously-granted scopes carry over)
 */
export function buildAuthorizeUrl(input: BuildAuthorizeUrlInput): string {
  const params = new URLSearchParams({
    client_id: input.clientId,
    redirect_uri: input.redirectUri,
    response_type: 'code',
    scope: REQUIRED_SCOPES,
    code_challenge: input.codeChallenge,
    code_challenge_method: 'S256',
    access_type: 'offline',
    prompt: 'consent',
    include_granted_scopes: 'true',
  });
  return `${GOOGLE_AUTHORIZE_ENDPOINT}?${params.toString()}`;
}

export function persistVerifier(verifier: string): void {
  try {
    sessionStorage.setItem(PKCE_VERIFIER_STORAGE_KEY, verifier);
  } catch {
    /* iOS Lockdown / private mode — swallow so the click handler proceeds */
  }
}

export function readVerifier(): string | null {
  try {
    return sessionStorage.getItem(PKCE_VERIFIER_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearVerifier(): void {
  try {
    sessionStorage.removeItem(PKCE_VERIFIER_STORAGE_KEY);
  } catch {
    /* non-fatal */
  }
}

function base64urlFromBytes(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) {
    s += String.fromCharCode(bytes[i]!);
  }
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
